import { AccountCustomCodes } from '@/components/CustomCodes/AccountCustomCodes';
import { SettingsTextField } from '@/components/settings/SettingsTextField.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { CustomCode as CustomCodeType } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Button from '@superfunnel/ui-core/v2/Button';
import CodeInput from '@superfunnel/ui-core/v2/CodeInput';
import { useContext, useEffect, useMemo, useState } from 'react';

export const CustomCode = () => {
  const [edit, setEdit] = useState<boolean>(false);
  const [showPublishReminder, setShowPublishReminder] =
    useState<boolean>(false);
  const { isSupportAdmin } = useContext(AuthContext);
  const { accountConfig, updateAccountConfig } = useContext(DashboardContext);
  const [customCodes, setCustomCodes] = useState<CustomCodeType[]>([]);

  // Legacy custom code fields
  const [headCustomCode, setHeadCustomCode] = useState<string>('');
  const [headCustomCodeDelay, setHeadCustomCodeDelay] = useState<number>(0);
  const [bodyCustomCode, setBodyCustomCode] = useState<string>('');
  const [bodyCustomCodeDelay, setBodyCustomCodeDelay] = useState<number>(0);

  // Check if we have any legacy data to show
  const hasLegacyData = useMemo(() => {
    return !!(
      accountConfig?.headCustomCode ||
      accountConfig?.bodyCustomCode ||
      accountConfig?.headCustomCodeDelay ||
      accountConfig?.bodyCustomCodeDelay
    );
  }, [accountConfig]);

  useEffect(() => {
    if (!accountConfig?.headCustomCode) return;
    setHeadCustomCode(accountConfig.headCustomCode);
  }, [accountConfig?.headCustomCode]);

  useEffect(() => {
    if (!accountConfig?.headCustomCodeDelay) return;
    setHeadCustomCodeDelay(accountConfig.headCustomCodeDelay);
  }, [accountConfig?.headCustomCodeDelay]);

  useEffect(() => {
    if (!accountConfig?.bodyCustomCode) return;
    setBodyCustomCode(accountConfig.bodyCustomCode);
  }, [accountConfig?.bodyCustomCode]);

  useEffect(() => {
    if (!accountConfig?.bodyCustomCodeDelay) return;
    setBodyCustomCodeDelay(accountConfig.bodyCustomCodeDelay);
  }, [accountConfig?.bodyCustomCodeDelay]);

  useEffect(() => {
    if (accountConfig?.customCodes === undefined) return;

    setCustomCodes(accountConfig.customCodes);
  }, [accountConfig?.customCodes]);

  // Helper to compare custom codes arrays
  const customCodesEqual = (a: CustomCodeType[], b: CustomCodeType[]) => {
    if (a.length !== b.length) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  useEffect(() => {
    if (!accountConfig) return;
    const savedCustomCodes = accountConfig?.customCodes ?? [];

    // Check for legacy field changes
    const legacyChanged =
      hasLegacyData &&
      ((accountConfig?.headCustomCode ?? '') !== headCustomCode ||
        (accountConfig?.headCustomCodeDelay ?? 0) !== headCustomCodeDelay ||
        (accountConfig?.bodyCustomCode ?? '') !== bodyCustomCode ||
        (accountConfig?.bodyCustomCodeDelay ?? 0) !== bodyCustomCodeDelay);

    setEdit(legacyChanged || !customCodesEqual(savedCustomCodes, customCodes));
  }, [
    accountConfig,
    customCodes,
    hasLegacyData,
    headCustomCode,
    headCustomCodeDelay,
    bodyCustomCode,
    bodyCustomCodeDelay,
  ]);

  const onDiscard = () => {
    setEdit(false);
    setCustomCodes(accountConfig?.customCodes || []);
    setHeadCustomCode(accountConfig?.headCustomCode || '');
    setHeadCustomCodeDelay(accountConfig?.headCustomCodeDelay || 0);
    setBodyCustomCode(accountConfig?.bodyCustomCode || '');
    setBodyCustomCodeDelay(accountConfig?.bodyCustomCodeDelay || 0);
  };

  const onSave = () => {
    setEdit(false);
    updateAccountConfig({
      customCodes,
      ...(hasLegacyData && {
        headCustomCode,
        headCustomCodeDelay,
        bodyCustomCode,
        bodyCustomCodeDelay,
      }),
    });
    // Show publish reminder after saving
    setShowPublishReminder(true);
  };

  return (
    <>
      <div
        className={
          'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
        }
      >
        <div className={'h-full overflow-y-auto'}>
          <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5 pb-5'}>
            <AccountCustomCodes
              customCodes={customCodes}
              onChange={setCustomCodes}
            />

            {hasLegacyData && (
              <>
                <SettingTitle
                  title="Legacy Custom Code"
                  description="These legacy fields are shown because they contain existing data. New custom codes should be added below."
                />
                <CodeInput
                  title={'Head'}
                  titleClassName={
                    'text-base-700 flex items-center gap-2 text-sm font-medium'
                  }
                  value={headCustomCode}
                  onChange={(v) => setHeadCustomCode(v)}
                  language={'javascript'}
                  inlineLines={5}
                  placeholder={'<!-- Add your custom code here -->'}
                />

                {isSupportAdmin && (
                  <SettingsTextField
                    title="Head Delay"
                    value={headCustomCodeDelay + ''}
                    setValue={(value: any) => {
                      setHeadCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
                    }}
                    pattern="^\d*$"
                    prefix="milliseconds"
                  />
                )}
                <CodeInput
                  title={'Body'}
                  titleClassName={
                    'text-base-700 flex items-center gap-2 text-sm font-medium'
                  }
                  value={bodyCustomCode}
                  onChange={(v) => setBodyCustomCode(v)}
                  language={'javascript'}
                  inlineLines={5}
                  placeholder={'<!-- Add your custom code here -->'}
                />

                {isSupportAdmin && (
                  <SettingsTextField
                    title="Body Delay"
                    value={bodyCustomCodeDelay + ''}
                    setValue={(value: any) => {
                      setBodyCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
                    }}
                    pattern="^\d*$"
                    prefix="milliseconds"
                  />
                )}

                <div className="border-base-200 my-4 border-t" />
              </>
            )}
          </div>
        </div>
      </div>
      {edit && (
        <div className={'w-full'}>
          <span
            className={
              'block h-px w-full bg-[linear-gradient(90deg,rgba(228,231,236,0)_0%,#E4E7EC_50.35%,rgba(228,231,236,0)_100%)]'
            }
          />
          <div
            className={
              'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
            }
          >
            <Button variant={'outline'} onClick={onDiscard}>
              Discard
            </Button>
            <Button onClick={onSave}>Save</Button>
          </div>
        </div>
      )}

      <AlertModal
        title="Custom Code Saved"
        open={showPublishReminder}
        onClose={() => setShowPublishReminder(false)}
        actions={[
          {
            type: 'primary',
            title: 'Got it',
            onClick: () => setShowPublishReminder(false),
          },
        ]}
        type="info"
      >
        <p>
          Your custom code changes have been saved. Published landing pages may
          need to be republished to reflect these changes.
        </p>
      </AlertModal>
    </>
  );
};
